import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import LoginPage from './pages/LoginPage'

// Route-level code-splitting: keep the landing (Dashboard) and login eager, lazy
// the rest so the initial bundle stays small (issue #112). The chart-heavy pages
// (Analytics, TrainingPlan, EventCommandCenter) benefit most.
const EventsManagement = lazy(() => import('./pages/EventsManagement'))
const EventDetail = lazy(() => import('./pages/EventDetail'))
const CreateEvent = lazy(() => import('./pages/CreateEvent'))
const Athletes = lazy(() => import('./pages/Athletes'))
const Results = lazy(() => import('./pages/Results'))
const Analytics = lazy(() => import('./pages/Analytics'))
const Settings = lazy(() => import('./pages/Settings'))
const RecruitingProfile = lazy(() => import('./pages/RecruitingProfile'))
const ScoutSearch = lazy(() => import('./pages/ScoutSearch'))
const EventCommandCenter = lazy(() => import('./pages/EventCommandCenter'))
const TrainingPlan = lazy(() => import('./pages/TrainingPlan'))

import { AuthProvider } from './contexts/AuthContext'
import { useAuth } from './hooks/useAuth'

function RouteFallback() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  )
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 2,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  )
}

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/athletes/:athleteId/profile" element={<RecruitingProfile />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <Layout>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/events" element={<EventsManagement />} />
          <Route path="/events/new" element={<CreateEvent />} />
          <Route path="/events/:eventId" element={<EventDetail />} />
          <Route path="/events/:eventId/command-center" element={<EventCommandCenter />} />
          <Route path="/athletes" element={<Athletes />} />
          <Route path="/athletes/:athleteId/profile" element={<RecruitingProfile />} />
          <Route path="/results" element={<Results />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/training/:planId?" element={<TrainingPlan />} />
          <Route path="/scout/search" element={<ScoutSearch />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}

export default App
